<template>
  <form @submit.prevent="submitContactDetails" class="p-6">
    <div v-if="hasPatientData" class="grid grid-rows mb-4">
      <h1 class="font-bold text-xl flex text-pdsPrimaryColor mt-0">Patiëntgegevens</h1>
      <div class="grid grid-rows lg:grid-cols-3 gap-2">
        <div class="col lg:col-span-2">
          <v-input-group-simple-2
            id="patientName"
            v-model:value="patientName"
            :rules="{
              required: false
            }"
            :disabled="true"
            type="text"
            label="Naam"
          />
        </div>
        <div class="">
          <v-input-group-simple-2
            id="birthDate"
            v-model:value="birthDate"
            :rules="{
              required: false
            }"
            :disabled="true"
            type="text"
            label="Geboortedatum"
          />
        </div>
      </div>
    </div>
    <div class="grid grid-rows gap-4">
      <div class="">
        <label for="treatmentAreaId">
          Selecteer hieronder het behandelgebied waarover u een vraag/opmerking heeft *
        </label>
        <model-select
          class="mt-2"
          id="treatmentAreaId"
          placeholder="Selecteer behandelgebied"
          v-model="treatmentAreaItem"
          :options="treatmentAreas"
          :selected-option="treatmentAreaItem"
          @blur="selectTratmentArea"
        >
        </model-select>
        <div class="form-group-simple__details" v-show="showTreatmentAreaErrorMessage">
          <div class="form-group-simple__messages">
            <div class="form-group-simple__error">* Dit veld is verplicht.</div>
          </div>
        </div>
      </div>
      <div class="">
        <label for="subjectId">Wat is het onderwerp van uw vraag *</label>
        <model-select
          class="mt-2"
          id="subjectId"
          placeholder="Selecteer onderwerp"
          search-text="search"
          v-model="subjectItem"
          :options="subjectTypes"
          :selected-option="subjectItem"
          @blur="selectSubject"
        >
        </model-select>
        <div class="form-group-simple__details" v-show="showSubjectErrorMessage">
          <div class="form-group-simple__messages">
            <div class="form-group-simple__error">* Dit veld is verplicht.</div>
          </div>
        </div>
      </div>
      <div class="">
        <legend class="-mb-3">Uw bericht *</legend>
        <v-textarea-group
          inputid="Uw bericht"
          @input="saveInput"
          v-model="contactDetails.body"
          :overwrite-value="contactDetails.body"
        />
        <div v-show="showErrorMessage" class="form-group-simple__details">
          <div class="form-group-simple__messages">
            <div class="form-group-simple__error">* Dit veld is verplicht.</div>
          </div>
        </div>
      </div>
    </div>
    <button
      :disabled="formSubmitted"
      class="flex self-end my-6 justify-self-end justify-center py-2 px-2 md:px-4 border border-primary-color shadow-sm text-xs md:text-sm lg:text-sm font-medium text-primary-color bg-white rounded-full hover:bg-primary-default/50 items-center"
    >
      <span class="font-bold"> Versturen</span>
      <ArrowRightIcon class="h-5 w-5 flex-shrink-0 ml-2 text-primary-default" aria-hidden="true" />
    </button>
  </form>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { ModelSelect } from 'vue-search-select';
import { tap } from 'rxjs/operators';
import { activePpwRecId$, availablePortals$, allTreatmentAreas$ } from '@/services';
import axios from 'axios';
import { ArrowRightIcon } from '@heroicons/vue/20/solid';
export default {
  name: 'ContactForm',
  components: {
    ModelSelect,
    ArrowRightIcon
  },
  props: {},
  emits: ['success'],
  setup(props, { emit }) {
    const store = useStore();
    const activePpwRecId = ref(null);
    const availablePortals = ref(null);
    const allTreatmentAreas = ref(null);
    const treatmentAreas = ref([]);
    const subjectTypes = [
      { text: 'Bestellen', value: 0 },
      { text: 'Levering', value: 1 },
      { text: 'Producten en/of assortiment', value: 2 },
      { text: 'Gebruik voorschrijfportaal', value: 3 }
    ];
    const contactDetails = ref({
      subject: '',
      body: '',
      ppwRecId: '',
      activePortalCode: '',
      treatmentAreaId: '0'
    });
    const subjectItem = ref({
      value: null,
      text: null
    });
    const treatmentAreaItem = ref({
      value: null,
      text: null
    });
    const showErrorMessage = ref(false);
    const showTreatmentAreaErrorMessage = ref(false);
    const showSubjectErrorMessage = ref(false);
    const formSubmitted = ref(false);

    const getTreatmentAreas = (values) => {
      values.forEach((element) => {
        let detail = {
          text: element.description,
          value: element.id
        };
        if (element.id !== '1011')
          // E40- 9216 - Filter Preventie-1011 treatment area for Contact Form only.
          treatmentAreas.value.push(detail);
      });
    };
    activePpwRecId$.subscribe((value) => {
      activePpwRecId.value = value;
    });
    availablePortals$.subscribe((value) => {
      availablePortals.value = value;
    });
    allTreatmentAreas$.subscribe((value) => (allTreatmentAreas.value = value));
    allTreatmentAreas$
      .pipe(
        tap((value) => {
          getTreatmentAreas(value);
        })
      )
      .subscribe();

    const patientData = computed(() => store.getters['patient/getPatientGegevens']);
    const patientName = computed(() => {
      const data = patientData.value;
      if (!data) return '';

      const { GeslachtId, Voorletters, Achternaam } = data;
      return `${aanhefFormat(GeslachtId)}${Voorletters} ${Achternaam}`;
    });
    const birthDate = computed(() => {
      const date = patientData.value?.GeboorteDatum;
      if (!date) return '';

      return new Date(date).toLocaleDateString('nl', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    });

    const hasPatientData = computed(() => !!Object.keys(patientData.value || {}).length);

    const aanhefFormat = (value) => {
      const titles = { 1: 'Dhr. ', 2: 'Mevr. ' };
      return titles[value] || '';
    };
    const selectTratmentArea = () => {
      showTreatmentAreaErrorMessage.value = false;
    };
    const selectSubject = () => {
      showSubjectErrorMessage.value = false;
    };
    const resetForm = () => {
      contactDetails.value = {
        subject: '',
        body: '',
        ppwRecId: '',
        activePortalCode: '',
        treatmentAreaId: '0'
      };
      treatmentAreaItem.value = {
        value: '',
        text: ''
      };
      subjectItem.value = {
        value: '',
        text: ''
      };
      formSubmitted.value = false;
    };
    const saveInput = (event) => {
      contactDetails.value.body = event?.target?.value || null;
      showErrorMessage.value = !contactDetails.value.body;
    };
    const submitContactDetails = async () => {
      const contact = contactDetails.value;
      contact.treatmentAreaId = treatmentAreaItem.value?.value ?? '0';
      contact.subject = subjectItem.value?.text ?? '';
      showTreatmentAreaErrorMessage.value = contact.treatmentAreaId === '0';
      showSubjectErrorMessage.value = !contact.subject;
      showErrorMessage.value = !contact.body;
      if (showTreatmentAreaErrorMessage.value || showSubjectErrorMessage.value || showErrorMessage.value) return;
      formSubmitted.value = true;
      contact.ppwRecId = activePpwRecId.value;
      contact.activePortalCode = availablePortals.value.activePortalCode;
      try {
        await axios.post('ContactForm/Send', contact).then(() => {
          emit('success');
          resetForm();
          formSubmitted.value = false;
        });
      } catch (error) {
        console.error('Failed to submit contact details:', error);
      }
    };

    return {
      patientData,
      subjectTypes,
      activePpwRecId,
      availablePortals,
      allTreatmentAreas,
      treatmentAreas,
      contactDetails,
      subjectItem,
      treatmentAreaItem,
      showErrorMessage,
      showTreatmentAreaErrorMessage,
      showSubjectErrorMessage,
      formSubmitted,
      patientName,
      birthDate,
      hasPatientData,
      aanhefFormat,
      selectTratmentArea,
      selectSubject,
      resetForm,
      saveInput,
      submitContactDetails
    };
  }
};
</script>
